/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetWsInterniParams,
  WsInterno,
  WsRispostaPaginataListWsInterno,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Cerca i servizi interni
 * @summary Cerca servizi interni
 */
export const getWsInterni = (
  params?: GetWsInterniParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListWsInterno>(
    { url: `/framework-cfg-utenti/ws-interni`, method: 'get', params, signal },
    options,
  );
};

export const getGetWsInterniQueryKey = (params?: GetWsInterniParams) =>
  [`/framework-cfg-utenti/ws-interni`, ...(params ? [params] : [])] as const;

export const getGetWsInterniQueryOptions = <
  TData = Awaited<ReturnType<typeof getWsInterni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetWsInterniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWsInterni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getWsInterni>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWsInterniQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWsInterni>>> = ({
    signal,
  }) => getWsInterni(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetWsInterniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getWsInterni>>
>;
export type GetWsInterniQueryError = ErrorType<unknown>;

/**
 * @summary Cerca servizi interni
 */
export const useGetWsInterni = <
  TData = Awaited<ReturnType<typeof getWsInterni>>,
  TError = ErrorType<unknown>,
>(
  params?: GetWsInterniParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getWsInterni>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetWsInterniQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna la configurazione di un servizio interno
 * @summary Aggiorna servizio interno
 */
export const putWsInterni = (
  wsInterno: WsInterno,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/ws-interni`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: wsInterno,
    },
    options,
  );
};

export const getPutWsInterniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putWsInterni>>,
    TError,
    { data: WsInterno },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putWsInterni>>,
  TError,
  { data: WsInterno },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putWsInterni>>,
    { data: WsInterno }
  > = props => {
    const { data } = props ?? {};

    return putWsInterni(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutWsInterniMutationResult = NonNullable<
  Awaited<ReturnType<typeof putWsInterni>>
>;
export type PutWsInterniMutationBody = WsInterno;
export type PutWsInterniMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna servizio interno
 */
export const usePutWsInterni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putWsInterni>>,
    TError,
    { data: WsInterno },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutWsInterniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Inserisce la configurazione per un servizio interno
 * @summary Inserisci servizio interno
 */
export const postWsInterni = (
  wsInterno: WsInterno,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<string>(
    {
      url: `/framework-cfg-utenti/ws-interni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: wsInterno,
    },
    options,
  );
};

export const getPostWsInterniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsInterni>>,
    TError,
    { data: WsInterno },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postWsInterni>>,
  TError,
  { data: WsInterno },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postWsInterni>>,
    { data: WsInterno }
  > = props => {
    const { data } = props ?? {};

    return postWsInterni(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostWsInterniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postWsInterni>>
>;
export type PostWsInterniMutationBody = WsInterno;
export type PostWsInterniMutationError = ErrorType<unknown>;

/**
 * @summary Inserisci servizio interno
 */
export const usePostWsInterni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postWsInterni>>,
    TError,
    { data: WsInterno },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostWsInterniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancella la configurazione di un servizio interno
 * @summary Cancella servizio interno
 */
export const deleteWsInterniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/ws-interni/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteWsInterniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWsInterniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteWsInterniId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteWsInterniId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteWsInterniId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteWsInterniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteWsInterniId>>
>;

export type DeleteWsInterniIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancella servizio interno
 */
export const useDeleteWsInterniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteWsInterniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteWsInterniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
